// Global command-palette search types (Contract A — CON-183).
//
// The frontend palette shell (this teammate), the rich
// preview pane (`drawer-preview-pane`, preview-reuse
// teammate), and the backend (`GET /v2/searches`) all agree
// on these shapes. Keep this file the single source of
// truth for the wire format.
//
// Props are camelCase on the React side — the backend writes
// snake_case and the `prop_transformer` / JSON serializer
// camelizes on the way out. `GET /v2/searches` returns plain
// JSON (not an Inertia visit), so the backend serializes
// camelCase keys directly.

import { msg } from "@lingui/core/macro"
import type { MessageDescriptor } from "@lingui/core"

// The preview pane (`drawer-preview-pane`, preview-reuse
// teammate) owns the canonical `PreviewTarget` definition.
// We re-export it as a type so there is exactly ONE
// definition shared by the palette and the pane (Contract C).
import type { PreviewTarget } from "@/components/v2/search/drawer-preview-pane"

export type { PreviewTarget }

// The type-filter tabs across the top of the palette. `all`
// is the default; the rest narrow the result set to a single
// entity kind. The backend echoes the requested tab and
// always returns counts for every tab so the strip can show
// per-tab badges without a second request.
export type SearchTab =
  | "all"
  | "conversations"
  | "contacts"
  | "companies"
  | "deals"
  | "notes"

// The concrete entity kind a result row carries. Note these
// are SINGULAR (`deal`, `contact`, …) while the filter tabs
// are PLURAL (`deals`, `contacts`, …) — a row is one entity,
// a tab is a collection. Contract A fixes this exact union.
export type SearchResultType =
  | "deal"
  | "contact"
  | "company"
  | "conversation"
  | "note"

export interface SearchResult {
  // Stable React key, e.g. "deal:42". Unique across the
  // result set even when two entities share an id.
  key: string
  type: SearchResultType
  title: string
  subtitle: string
  // Quoted match excerpt for note / conversation results.
  // `null` for entity kinds that don't carry a body.
  snippet: string | null
  // Right-aligned relative-time label (e.g. "2h", "Mar 14").
  // `null` when the entity has no meaningful timestamp.
  timestampLabel: string | null
  // Desktop right-pane preview target. Notes preview their
  // parent deal/contact and conversations preview their contact,
  // so those rows DO carry a target. It's `null` only when there
  // is no parent to render (e.g. an orphaned conversation); such
  // rows still navigate via `href` on Enter / tap.
  previewTarget: PreviewTarget | null
  // Enter / mobile-tap destination — a full v2 page.
  href: string
}

export interface SearchResponse {
  query: string
  // Echoes the requested filter.
  type: SearchTab
  // Per-tab result counts. Keyed by every `SearchTab`.
  counts: Record<SearchTab, number>
  results: SearchResult[]
}

// Cold-start suggestion (CON-186) — a navigate-only row seeded
// from the business's recent records, shown on first run before
// the user has built any localStorage history. The same row
// subset a `SearchResult` carries, minus the search-only fields
// (no snippet / timestamp / previewTarget — the cold list is
// navigate-only, never previewed).
export type SuggestionItem = Pick<
  SearchResult,
  "key" | "type" | "title" | "subtitle" | "href"
>

export interface SuggestionsResponse {
  suggestions: SuggestionItem[]
}

// The tab order rendered in the palette strip, left to
// right. `all` first, then the entity tabs.
export const SEARCH_TABS: readonly SearchTab[] = [
  "all",
  "conversations",
  "contacts",
  "companies",
  "deals",
  "notes",
] as const

// Human labels for each tab.
export const SEARCH_TAB_LABELS: Record<SearchTab, MessageDescriptor> = {
  all: msg`All`,
  conversations: msg`Conversations`,
  contacts: msg`Contacts`,
  companies: msg`Companies`,
  deals: msg`Deals`,
  notes: msg`Notes`,
}

// Minimum query length before the palette issues a search.
// Below this we stay in the cold-start (recents) view.
export const SEARCH_MIN_QUERY_LENGTH = 2
